import { NumberRange } from "scichart/Core/NumberRange";
import { Spacetime } from "spacetime";

import { MainMode } from "./shared/dp";
import { IUserDetails, IUserSettings } from "./shared/user";

export interface ICctvSource {
  camId: string;
  camName: string;
  vesselId: string;
  isStreaming?: boolean;
}
export interface IVesselData {
  alphaId: string;
  vesselId: string;
  vesselName: string;
}
export interface IDataSystem {
  dataSystemId: number;
  dataSystemName: string;
}

export interface IVesselSensorLimitsNavigationInitialData {
  vesselId: string;
  dpId: number;
}

export interface ISystemDataCountResponse {
  dataCount: number;
  weekNo: number;
  timeFrom: string;
  timeTo: string;
}

export interface IPbStartVesselCardData {
  id: string;
  name: string;
  imageSrc: string;
}

export interface IDataPointState {
  dpCat: string;
  cat: string;
  id: number;
  dpLabel: string;
  label: string;
  tag: string;
  seq: number;
  unit: string;
  dataType: string;
  value?: string | number;
  checked?: boolean;
  alarm?: number;
  filtered?: boolean;
}

export interface IAlarmData {
  alarmId: number;
  alarmEnd: string;
  threshold: TThresholdType;
  alarmStart: string;
  thresholdId: number;
  thresholdValue: number;
  dpId: number;
  severity: EAlarmSeverity;
}

export interface IVesselInfoPageData {
  data: {
    status: boolean;
    name: string;
    imageUrl: string;
    type: string;
    designSeries: string;
    imoNo: string;
    hull: string;
    classId: string;
    classSociety: string;
    formerName: string;
    operator: string;
    yard: string;
    owner: string;
    client: string;
    clientTels: Array<{ name: string; no: string }>;
    clientEmails: Array<{ name: string; email: string }>;
    vesselTels: Array<{ name: string; no: string }>;
    vesselEmails: Array<{ name: string; email: string }>;
  };
  meta: {
    vesselId: string;
  };
}

export interface IAlarmDataResponse {
  [key: string]: [IAlarmData];
}

export type IVesselAlarmSystemListAPIResponse = {
  dataPointInfo: Record<number, IDataPointConfig>;
  dpDesc: Record<string, APIDataPointDesc>;
} & Record<string, IAlarmRowData[]>;

export interface IAlarmSystemData {
  level: string;
  alarmId: number;
  valLast: number;
  valAvg2D: number;
  valMax2D: number;
  valMin2D: number;
  ackStatus: boolean;
  valAvg24H: number;
  alarmSystem: string;
  alarmCount7D: number;
  alarmCount24H: number;
  alarmCountCurrent: number;
  alarmDetails: IAlarmDetail[];
}

export interface IAlarmHistoryItem {
  dpId: number;
  thresholdId: number;
  alarmEnd: string;
  threshold: string;
  alarmStart: string;
}

export type IAlarmHistoryAPIResponse = Record<string, IAlarmHistoryItem[]>;

export interface APIDataGroup {
  dataSystemId: number;
  mainGroupId: number;
  mainGroupName: string;
  mainGroupNo: number;
  subGroupId: number;
  subGroupName: string;
  subGroupNo: number;
  typeGroupId: number;
  typeGroupName: string;
}

export interface ITableColumnHeader {
  name: string;
  key: string;
}

export interface IAlarmThresholdsForChart {
  [key: string]: IDataPointAlarmThreshold;
}

export interface APIAlarmThreshold {
  typeId: number;
  configId: number;
  operator: ">" | "<";
  runConId: number;
  typeName: TThresholdType;
  alarmName: TThresholdType;
  threshold: number;
  dataPointId: number;
  severitySeq: number;
  thresholdId: number;
}

export interface IDashboardCardResponse {
  data: {
    config: {
      backgroundImage?: string;
      title?: string;
      cards: IDashboardCard[];
      nodes: IChartElement[];
      connections: INodeConnection[];
    };
  };
  title: string;
  created: string;
  updated: string;
  configId: number;
  isEditable: boolean;
  isPublic: boolean;
  vesselId: string;
  typeId: number; // 1 = Advanced View, 2 = Dashboard
}

export type APIGetTrendDataResponse = Array<{
  data: APITrendDataPoint[];
  dbId: string;
  alarmThreshold: Record<string, Record<TAlarmTypeId, APIAlarmThreshold>>;
}>;

export type IStaticAlarmData = null | {
  id: string;
  name: string;
  threshold: Record<
    TThresholdType,
    {
      threshold: number | "";
      thresholdId: number | "";
    }
  >;
};

export interface IReportComment {
  user: string;
  title: string;
  date: Date;
  comment: string;
}

export interface IReportContent {
  alarmRangeStart: Spacetime;
  alarmRangeEnd: Spacetime;
  repairTimeStart: Spacetime;
  repairTimeEnd: Spacetime;
  preparationTimeStart: Spacetime;
  preparationTimeEnd: Spacetime;
  failureMode: string;
  failureModeInfo: string;
  rootCause: string;
  rootCauseInfo: string;
  equipmentManufacturer: string;
  other: string;
  title: string;
  runningTime: number;
  runningTimeUnit: string;
  tag: string;
  componentValue: string;
  equipmentValue: string;
  partValue: string;
  subEquipmentValue: string;
  systemValue: string;
  status: EReportStatus;
  priority: EReportPriority;
  vesselId: string;
  vesselName: string;
  id: number;
  description: string;
  dueAt: Date;
  isPlanned: boolean;
  downtime: number;
  groupName: string;
  subgroupName: string;
  owner: string;
}

export interface IReportRequestBody {
  vesselId: string;
  dataPointId: string;
  status: number;
  priority: number;
  title: string;
  runningTime: string;
  runningTimeUnit: string;
  alarmRangeStart?: Date;
  alarmRangeEnd?: Date;
  repairTimeStart?: Date;
  repairTimeEnd?: Date;
  preparationTimeStart?: Date;
  preparationTimeEnd?: Date;
  failureMode: string;
  failureModeInfo: string;
  rootCause: string;
  rootCauseInfo: string;
  description: string;
  equipmentManufacturer: string;
  downtime: string;
  isPlanned: boolean;
  other: string;
  groupId: string;
  subgroupId: string;
  // TODO Remove after proper API
  owner: string;
  vesselName: string;
  groupName: string;
  subgroupName: string;
  comments?: IReportComment[];
  tag: string;
  systemValue: string;
  equipmentValue: string;
  subEquipmentValue: string;
  componentValue: string;
  partValue: string;
}

export interface IEventHistoryRequestBody {
  timeFrom: Date;
  timeTo: Date;
  vesselId: string;
  dPoints?: number[];
  eventType?: TEvent[];
}

export type IEventLogResponse = Record<string, IEventHistoryItem[]>;

export interface IEventHistoryItem {
  dataPoint: null | number;
  vesselId?: string;
  eventType: TEvent;
  registered: string;
  reportId?: number;
  alarmId?: number;
  thresholdId?: number;
  commandId?: number;
  noteId?: number;
  ackId?: number;
  bridgeLockId?: number;
}

export interface IReportData {
  data: IReportContent;
  meta: {
    created: Date;
    updated: Date;
    reportId: number;
    vesselId: string;
    reportStatus: EReportStatus;
  };
  comments: IReportComment[];
}

export interface IFilter {
  mainGroupId: number;
  mainGroupNo: number;
  subGroupId: number;
  subGroupNo: number;
  typeGroupId: number;
}

export interface ISystemModule {
  accessNo: number;
  moduleId: number;
  moduleName: string;
}

export interface ISystemFunction {
  accessNo: number;
  functionId: number;
  functionName: string;
}

export interface IVesselToggleItem<
  T extends IUserAccess | ICompanyAccess | IUserAccess[] | ICompanyAccess[]
> {
  isNew?: boolean;
  isUpdated?: boolean;
  isAdded?: boolean;
  checkStatus?: ETriCheckbox;
  vesselName: string;
  access: T;
}

export interface ICompanyAccess {
  vesselId: string;
  vesselName: string;
  companyId: number;
  moduleIdAccess: number[];
  companyAccessId: number;
  datapointAccess: number[];
}

export interface ICompany {
  companyId: number;
  companyName: string;
  companyDetails: Record<string, string>;
  companyProfileId: EAccessLevel;
  companyProfileName: TAccessLevel;
}

export interface ICompanyProfile {
  profileId: number;
  profileName: TAccessLevel;
  functionIdRead: number[] | null;
  functionNoRead: number;
  functionIdWrite: number[] | null;
  functionNoWrite: number;
  functionIdDelete: number[] | null;
  functionIdUpdate: number[] | null;
  functionNoDelete: number;
  functionNoUpdate: number;
}

export type INoteResponseData = Record<string, Record<string, INoteData[]>>;

export interface INoteData {
  note: {
    body: string;
    heading: string;
    createdBy: string;
    createTime: string;
  };
  noteMeta: {
    dbId: string;
    tdMax: string;
    tdMin: string;
    dataTime: string;
    noteId: number;
    dataPoint: number;
  };
}

export interface INoteFetchParams {
  dbId: string;
  timeTo?: Date;
  timeFrom?: Date;
  dPoints?: number[];
  noteId?: number;
}

export interface IAlarmDatapointRowCheckedEAEvent {
  vesselId: string;
  dpId: number;
  checked: boolean;
  isAcked: boolean;
  highestAlarm: EVesselStatus.Alarm | EVesselStatus.Warning;
}

export interface INoteCreateParams {
  authorName: string;
  data: {
    note: {
      body: string;
      heading: string;
    };
    noteMeta: {
      dbId: string;
      tdMin: string;
      tdMax: string;
      dataPoint: number;
    };
    noteId?: number;
  };
}

export interface IAckFormData {
  text: string;
  status: string;
  cycle: TAckCycle;
}

export type TAckCycle = "non" | "1w" | "24h";

export interface IAckRequestData {
  meta: {
    vesselId: string;
    cycleFrom: Date;
    cycleTo: Date;
    dataPoint: number;
  };
  ack: {
    unack?: boolean;
    comment: string;
    status: string;
    alarmType: string;
    alarmStatus: EVesselStatus.Alarm | EVesselStatus.Warning;
  };
}

export interface IAckResponseData {
  meta: {
    vesselId: string;
    cycleFrom: string;
    cycleTo: string;
    ackId: number;
    createBy: string;
    dataPoint: number;
    userId: number;
    createTime: string;
  };
  ack: {
    unack?: boolean;
    comment: string;
    status: TEquipmentStatus;
    alarmStatus: EVesselStatus.Alarm | EVesselStatus.Warning;
    alarmType: string;
  };
}

export interface IMtDropdownData<T = string> {
  name: string;
  value: T;
  metadata?: string;
}

export interface IGroupItem<T_Id = number, T_Metadata = string> {
  name: string;
  id: T_Id;
  metadata: T_Metadata;
}

export interface IGroupedData<T_Id = number, T_Metadata = string> {
  group: string;
  items: IGroupItem<T_Id, T_Metadata>[];
}

export interface IZoomHistory {
  range: NumberRange;
  data: {
    [key: number]: TrendDataPoint[];
  };
}

export interface TrendDataPoint {
  x: number;
  y: number;
  metadata?: APITrendDataPointData;
}

export interface IVesselDataHealth {
  [key: string]: Record<TDataSystem | null, ISystemDataCountResponse[]>;
}

export interface IAnnotationModalParams {
  timeStampMin: string;
  timeStampMax: string;
  dataPointId: string;
  vesselId: string;
  dataPointName: string;
}

export interface APITrendDataPointData {
  tdMax: string;
  tdMin: string;
  valAvg: number;
  valMax: number;
  valMin: number;
  valAvgT: number;
  sourceCount: number;
}

export type APITrendDataPoint = Record<string, APITrendDataPointData> & {
  t: string;
};

export interface IPlaybackData {
  alarmCount: number;
  name: string;
  sourceName: string;
  value: number;
  datapointId: number;
}

export interface ICardVisibilities {
  avgGenLoad: boolean;
  avgPrAvgSpeed: boolean;
  avgDraft: boolean;
  totalEmission: boolean;
  totalCo2: boolean;
  totalNox: boolean;
  totalSox: boolean;
}

export interface IChartSettings {
  sameScale: boolean;
  showCandles: boolean;
  showTooltip: boolean;
  yAxesOn: Record<string, boolean>;
  limitsOn: Record<string, boolean>;
  points: boolean;
  lineWidth: number;
  autoScale: boolean;
}

export interface IVesselStateRequestBody {
  authorName: string;
  data: {
    meta: { vesselId: string; cycleFrom?: Date; cycleTo?: Date };
    command: {
      vesselPause?: boolean;
      vesselDisable?: boolean;
      comment?: string;
      reason?: TDisableReason;
    };
  };
}

export interface IVesselRowData {
  id: string;
  alphaId: string;
  vessel: string;
  speed: number;
  distToRig: number;
  avgFuelBurn: number;
  alarmCount: number;
  totalFuelBurn: number;
  mode: EVesselStatus;
  comm: number;
  state: number;
  vesselPause: boolean;
  vesselDisable: boolean;
  vesselState: IVesselState | Record<string, never>;
}

export interface IVesselState {
  meta: {
    userId: number;
    cycleFrom: string;
    cycleTo: string;
    createBy: string;
    vesselId: string;
    commandId: number;
    createTime: string;
  };
  command: {
    status: string;
    comment?: string;
    vesselDisable?: boolean;
    vesselPause?: boolean;
    reason?: TDisableReason;
  };
}

export interface IAlarmRowData {
  tag: number;
  valLast: number;
  ackStatus: Array<IAckResponseData | null>;
  followStatus: boolean;
  valAvg24H: number;
  valMax24H: number;
  valMin24H: number;
  alarmCount7D: number;
  alarmCount24H: number;
  alarmCountCurrent: number;
  alarmDetails: IAlarmDetail[];
}

export interface IAlarmDetail {
  severity: EAlarmSeverity;
  alarmName: string;
  alarmCount7D: number;
  alarmCount24H: number;
  alarmCountCurrent: number;
}

export interface ILegendItemData {
  vesselName: string;
  groupName: string;
  dataPointSourceName: string;
  dataPointName: string;
  id: string;
  isVisible: boolean;
  isAvailable: boolean;
  unit?: string;
}

export interface APIDataPoint {
  dataPointId: number;
  mainGroupId: number;
  mainGroupNo: number;
  measPointId: number;
  measPointNo: number;
  subGroupId: number;
  subGroupNo: number;
  typeGroupId: number;
  measPointName: string;
  vesselId: string;
  cat: string;
  label: string;
  metricId: number;
}

export interface ITimelineData {
  time: Date;
  data: IPlaybackData[];
}

export interface ISettingsSidebarSection {
  id: string;
  isToggled: boolean;
  name: string;
  subsections: Array<{ id: string; name: string }>;
  accessLevel: EAccessLevel;
}

export interface ICobrasJob {
  data: {
    dataPointC: number;
    dataPointsX: number[];
    dataPointsY: number[];
  };
  meta: {
    jobId: string;
    vesselId: string;
    status: string;
    statusId: ECobrasJobStatus;
    registered: Date;
    dataTimeEnd: Date;
    jobStarted: Date;
    jobCompleted: Date;
    dataTimeStart: Date;
  };
}

export interface ICobrasJobConfig {
  vesselId: string;
  dataPointC: number;
  dataPointsY: number[];
  dataPointsX: number[];
}

export interface ICobrasJobResultData {
  dataPointsX: number[];
  linearRegression: { rmsd: number; smape: number; rSquared: number };
}

export interface ICobrasJobResult {
  dataPointC: number;
  dataPointY: number;
  dbId: string;
  family: ICobrasJobResultData;
  jobId: string;
  standard: ICobrasJobResultData;
  status: string;
  version: number;
}

export interface ISearchableSidebarItem {
  id: string;
  isToggled: boolean;
  name: string;
  items: ISearchableSidebarItem[];
}

export type APIDataPointDesc = Omit<
  APIDataPoint & {
    subGroupName: string;
    mainGroupName: string;
    typeGroupName: string;
  },
  "vesselId" | "cat" | "label" | "metricId"
>;

export interface IEditUserTableData {
  id: string;
  name: string;
  company: string;
  accessLevel: "basic" | "basic+" | "admin";
  position: string;
}

export interface ICompanyUser {
  userId: number;
  companyId: number;
  profileId: number;
  companyName: string;
  profileName: string;
  userDetails: { details: IUserDetails; settings: IUserSettings };
  companyUserId: number;
}

export interface IEditScheduleTable {
  name: string;
  id: string;
  vessels: {
    name: string;
    id: string;
  }[];
}
export interface IAccessProfile {
  modules: {
    dp: boolean;
    ibs: boolean;
    trends: boolean;
    locator: boolean;
    events: boolean;
    fuel: boolean;
    customDashboard: boolean;
    playback: boolean;
    cctv: boolean;
    statisticsAndAnalytics: boolean;
  };
  user: {
    manage: boolean;
    create: boolean;
    accessRestriction: boolean;
  };
  vessel: {
    profile: boolean;
    alarm: boolean;
  };
}

export interface IFuelData {
  consumers: Record<string, number>;
  startTime: string;
  endTime: string;
  mainMode: MainMode;
  secondsInMode: number;
  totalConsumption: number;
  totalConsumptionPerSecond: number;
}

export interface IFuelDataResponse {
  config: Array<{
    metric: Omit<IDataPointConfig, "dpLabel" | "dpCat">;
    datapoint: Pick<IDataPointConfig, "label" | "cat" | "id">;
  }>;
  data: IFuelData[];
}

export interface IUserAccess {
  companyAccessId: number;
  companyId: number;
  userAccessId: number;
  userId: number;
  datapointAccess: number[];
  moduleIdAccess?: number[];
  companyName: string;
  userEmail: string;
  vesselId: string;
  vesselName: string;
}

export type IVesselDatapointsListItem = {
  dpId: number;
  name: string;
  mainGroup: string;
} & Record<string, number>;

export interface InitialChartData {
  vesselIds: string[];
  dataPointIds: Array<string | number>;
  timeFrom?: string;
  timeTo?: string;
}

export interface IMtLabeledToggleButton {
  id: string;
  label: string;
}

export type TAlarmTypeId = "1" | "3" | "5" | "6";
export type TEvent =
  | "alarmThreshold"
  | "report"
  | "alarmTriggered"
  | "alarmAck"
  | "dataNote"
  | "bridgeLock"
  | "vesselControl";
export interface IGetDataVesselData {
  alarmThreshold?: Record<string, IDataPointAlarmThreshold>;
  data?: APITrendDataPoint[];
  dataPointInfo?: Record<number | string, IDataPointConfig>;
}

export interface IUiSettings {
  isSidebarOpen: boolean;
  isSourceDataName: boolean;
  isSidebarByVessel: boolean;
  isDarkThemeOn: boolean;
  language: TLanguage;
}

export interface IGetDataResponse {
  dpDesc: Record<string, APIDataPointDesc>;
  vessels?: Record<string, IGetDataVesselData>;
}

export type IDataPointAlarmThreshold = Partial<Record<TAlarmTypeId, APIAlarmThreshold | never>>;

export interface IUserSettingsUpdateRequestParams {
  emailNotifications?: boolean;
  mobileNotifications?: boolean;
  darkTheme?: boolean;
  units?: TUnit;
}

export interface IGridLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

// The datapoint configuration data from db
export interface IDataPointConfig {
  id: number;
  cat: string;
  dpCat: string;
  dpLabel: string;
  label: string;
  tag: string;
  seq: number;
  unit: string;
  dataType: string;
  metricId?: number;
}

export interface IBackofficeDataPointConfig {
  tag: string;
  dpId: number;
  name: string;
  unit: string | null;
  checksum: string;
  dataType: string;
  metricId: number;
  sentence: number;
  sequence: number;
  vesselId: string;
  dataSystemId: number;
  subSystemName: string;
  dataSystemName: string;
}

// The modified/extended version of it to be used on dashboards/advanced datahub
export interface IDataPointCustomConfig extends IDataPointConfig {
  alias?: string;
  icon?: string;
  system: TDataSystem;
  min?: string;
  max?: string;
}

export interface IDashboardConfigListItem {
  typeId: number;
  configId: number;
  title: string;
  created: string;
  updated: string;
  typeName: string;
  vesselId: string;
}

export interface IDashboardCard {
  id: string;
  title: string;
  topIndex?: number;
  layout?: IGridLayout;
  dataPoints: IDataPointCustomConfig[];
  cardType: TCardType;
  backgroundImage?: string;
  vesselId: string;
  dataRange?: TCardRangeOption;
  barChartScaleMin?: string;
  barChartScaleMax?: string;
  visible?: boolean;
}

export interface IDashboardConfig {
  backgroundImage?: string;
  cards: IDashboardCard[];
  title: string;
}
// TODO merge IDashboardCardResponse into this
export interface IDashboardConfigResponse {
  data: {
    config: IDashboardConfig;
  };
  created: string;
  updated: string;
  configId: number;
  vesselId: string;
}

export interface IContactData {
  firstName: string;
  lastName: string;
  // id: string;
  email: string;
}

export interface IChipData {
  firstName: string;
  lastName: string;
  // id: string;
  email: string;
}

export type IAlarmRequestParams =
  | IAlarmReadRequestParams
  | IAlarmWriteRequestParams
  | IAlarmUpdateRequestParams
  | IAlarmDeleteRequestParams;

export interface IAlarmReadRequestParams {
  req: "read";
  dbId: string;
  dPoints: number[];
}

export interface IAlarmWriteRequestParams {
  req: "write";
  dbId: string;
  input: {
    dPoints: number;
    threshold: number;
    configId: EAlarmConfig;
  }[];
}

export interface IAlarmUpdateRequestParams {
  req: "update";
  dbId: string;
  input: {
    thresholdId: number;
    threshold: number;
  }[];
}

export interface IAlarmDeleteRequestParams {
  req: "delete";
  dbId: string;
  input: {
    thresholdId: number;
  }[];
}

export interface ICobrasAlarmData {
  id: string;
  tagName: string;
  value: number;
}

export interface ICreateUserAccessRequestData {
  userId: number;
  vesselId: string;
  moduleIdAccess?: number[];
  moduleAccessNo?: number;
  datapointAccess?: number[];
  userAccessId?: number;
}
export interface IReadUserAccessRequestData {
  companyId?: number[];
  userId?: number;
  vesselId?: string[];
}
export interface IUpdateUserAccessRequestData {
  userAccessId: number;
  moduleIdAccess?: number[];
  moduleAccessNo?: number;
  datapointAccess?: number[];
}
export interface IDeleteUserAccessRequestData {
  userAccessId: number;
}

export interface ICreateCompanyAccessRequestData {
  vesselId: string;
  moduleIdAccess?: number[];
  moduleAccessNo?: number;
  datapointAccess?: number[];
  companyAccessId?: number;
}
export interface IReadCompanyAccessRequestData {
  companyId?: number;
  userId?: number;
  vesselId?: string[];
}
export interface IUpdateCompanyAccessRequestData {
  companyAccessId: number;
  moduleIdAccess?: number[];
  moduleAccessNo?: number;
  datapointAccess?: number[];
}
export interface IDeleteCompanyAccessRequestData {
  companyAccessId: number;
}

export interface IUserAccessRequestData {
  data: TUserAccessRequestData;
  req: TDatabaseFunction;
}

export interface ICompanyAccessRequestData {
  data: TCompanyAccessRequestData;
  req: TDatabaseFunction;
}

export interface IKeyGenResponse {
  vessel_id: string;
  user_id: number;
  gen_code: string;
  duration: number;
  registered: string;
  access_reason: string;
  close_status: boolean;
  email: string;
}

export interface IKeyStatusResponse {
  vessel_id: string;
  user_id: number;
  key: string;
  duration: number;
  registered: string;
  access_reason: string;
  close_status: boolean;
  email: string;
}

export interface ICloseKeyRequest {
  vessel_id: string;
  close_message: string;
}

export type TUserAccessRequestData =
  | ICreateUserAccessRequestData
  | IReadUserAccessRequestData
  | IUpdateUserAccessRequestData
  | IDeleteUserAccessRequestData;

export type TCompanyAccessRequestData =
  | ICreateCompanyAccessRequestData
  | IReadCompanyAccessRequestData
  | IUpdateCompanyAccessRequestData
  | IDeleteCompanyAccessRequestData;

export type TUnit = "metric" | "imperial";
export type TChartType = "line" | "spline" | "area" | "spline-area";
export type TFilterKey = "acked" | "unacked" | "inalarm" | "lastweek" | "last24h" | "ok" | "follow";
export type TSortKey = "date" | "alarm" | "acked" | "az" | "location" | "follow" | "custom";

export type TEquipmentStatus = "ok" | "observation" | "danger" | "warning" | "caution";

export type TDisableReason = "cold" | "warm" | "sold" | "other";

export type TThresholdType = "high" | "high high" | "low" | "low low";

export type TCardType = "pie" | "trend" | "regular" | "bar";

export type TCardRangeOption = "live" | "avg" | "min" | "max";

export type TAccessLevel = "basic" | "admin" | "super admin";

export type TLanguage = "en" | "pt_br";

export type TDataSystem = "IBS" | "CBS" | "DPS" | "TCS" | "GWS";

export type TDatabaseFunction = "write" | "update" | "read" | "delete";

type TDirection = "left" | "right" | "top" | "bottom";

export type IChartElement = Partial<{
  id: string;
  layout: IGridLayout;
  rotation: number;
  icon: string;
  iconNegative: string;
  color: string;
  colorNegative: string;
  text: string;
  statusKey: [TDataSystem, number];
  content: string;
  type: "switch" | "label" | "icon" | "instrument";
  instrument: "mt-azi" | "mt-tunnel";
}>;

export interface INodeConnection {
  from: string;
  to: string;
  statusKey: [TDataSystem, number];
  rotation?: number;
  fromSocket?: TDirection;
  toSocket?: TDirection;
}

export enum EVesselStatus {
  Disabled = -1,
  Paused,
  Running,
  Warning,
  Alarm,
  Summary,
}

export enum ETriCheckbox {
  Unchecked,
  Checked,
  Indeterminate,
}

export enum EAccessLevel {
  Basic = 1,
  BasicPlus,
  Admin,
  SuperAdmin,
}

export enum EUnit {
  Imperial,
  Metric,
}

export enum EReportPriority {
  None,
  Low,
  Medium,
  High,
}

export enum EReportStatus {
  Closed,
  Open,
  OnHold,
}

export enum ESchedulePattern {
  Daily,
  Weekly,
  Monthly,
  Yearly,
}

export enum EAlarmConfig {
  High = 1,
  HighHigh = 5,
  Low = 3,
  LowLow = 6,
}

export enum EAlarmSeverity {
  Low = 2,
  High = 3,
}

export enum EQueryFlow {
  Race,
  Latest,
  InOrder,
}

export enum EDashboardType {
  AdvDhub = 1,
  Dashboard,
}

export enum ECobrasJobStatus {
  Created = 1,
  Queued,
  Processing,
  Complete,
  Failed,
  Abort,
  Deleted,
}

export enum EModuleId {
  Landing = 0,
  Alarms,
  CCTV,
  Dashboards,
  Data,
  DP,
  Fuel,
  Locator,
  Helm,
  Network,
  Playback,
  Reporting,
  Trends,
  Event,
  Fleet,
  NESTI4,
  Vessel,
  COBRAS,
  Backoffice = 19,
  Settings = 100,
}
